/**
 * An assistant picked on /login before the visitor had an account (GET-18726).
 *
 * Every endpoint that saves a pick needs a user, so the choice waits here until
 * the OAuth callback applies it. localStorage rather than sessionStorage: the
 * round-trip can land in another tab.
 */
import {
  OnboardingWizardEvents,
  trackEvent
} from '../analytics/posthog'
import {
  authService
} from '../services/api'
import {
  clearCustomCharacterDraft,
  isPersonalityTraits
} from './customCharacterDraft'
import type {
  AssistantCharacter,
  CustomCharacterDetails,
  User
} from '../services/api'

export type PresignupAssistantChoice =
  | { kind: 'builtin'; character_id: number }
  | ({ kind: 'custom' } & CustomCharacterDetails)

const STORAGE_KEY = 'catch_presignup_assistant'
// Long enough for an OAuth round-trip or a coffee break; short enough that a
// pick abandoned on a shared machine is not applied to someone else's signup.
const MAX_AGE_MS = 24 * 60 * 60 * 1000

interface StoredChoice {
  choice: PresignupAssistantChoice
  saved_at: number
}

function isChoice(value: unknown): value is PresignupAssistantChoice {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  if (record['kind'] === 'builtin') return typeof record['character_id'] === 'number'
  return record['kind'] === 'custom'
    && typeof record['name'] === 'string'
    && typeof record['bio'] === 'string'
    && isPersonalityTraits(record['personality_traits'])
}

export function savePresignupAssistant(choice: PresignupAssistantChoice): void {
  try {
    const stored: StoredChoice = { choice, saved_at: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // Storage full or blocked: the wizard's character step simply runs as usual.
  }
}

export function loadPresignupAssistant(): PresignupAssistantChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { choice, saved_at: savedAt } = parsed as Partial<Record<keyof StoredChoice, unknown>>
    if (typeof savedAt !== 'number' || Date.now() - savedAt > MAX_AGE_MS) return null
    return isChoice(choice) ? choice : null
  } catch {
    return null
  }
}

export function clearPresignupAssistant(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear.
  }
}

/**
 * Save the stored pick to the signed-in user. A custom character is created
 * and auto-selected by the backend in one call.
 */
export async function applyPresignupAssistant(
  choice: PresignupAssistantChoice,
): Promise<AssistantCharacter> {
  if (choice.kind === 'builtin') {
    return authService.updateAssistantCharacter(choice.character_id)
  }
  const { kind: _kind, ...details } = choice
  const character = await authService.createCustomCharacter(details)
  // The builder kept its draft in case this failed; it is saved now.
  clearCustomCharacterDraft(null)
  return character
}

/**
 * Apply a stored pick to a user who has not chosen an assistant yet, and drop
 * it either way. The wizard then derives past its character step on its own.
 * An onboarded or already-chosen account keeps what it has. On failure the
 * step simply runs, and a custom pick's details are still in the builder's
 * draft.
 */
export async function applyStoredPresignupAssistant(user: User): Promise<User> {
  const choice = loadPresignupAssistant()
  if (choice == null) return user
  clearPresignupAssistant()
  if (user.setup_completed_at != null || user.selected_character_id != null) return user
  try {
    const character = await applyPresignupAssistant(choice)
    trackEvent(OnboardingWizardEvents.PRESIGNUP_CHARACTER_APPLIED, { kind: choice.kind })
    return { ...user, selected_character_id: character.id }
  } catch (err) {
    console.error('Failed to apply pre-signup assistant:', err)
    trackEvent(OnboardingWizardEvents.PRESIGNUP_CHARACTER_APPLY_FAILED, { kind: choice.kind })
    return user
  }
}

const LANDING_HOSTS = new Set(['catchagent.ai', 'www.catchagent.ai'])

/**
 * Whether this visit is a new signup arriving from marketing: a click through
 * from the landing site (its CTAs send `?source=`, `?provider=` or nothing, so
 * only the referrer identifies them), an ad, which links straight to /login
 * and arrives with no referrer but a `utm_*` tag, or a shared referral link
 * (`?ref=`), which is usually pasted and carries neither. A plain /login, a sign-in
 * failure coming back, and the app's own signed-out redirects
 * (`?source=connect-inbox` and the like) have neither, and are people signing
 * back in. Our emails carry `utm_medium=email` and go to existing users.
 */
export function arrivedFromMarketing(searchParams: URLSearchParams, referrer: string): boolean {
  if (searchParams.has('error')) return false
  let referrerHost: string | null = null
  try {
    referrerHost = referrer ? new URL(referrer).hostname : null
  } catch {
    referrerHost = null
  }
  if (referrerHost !== null && LANDING_HOSTS.has(referrerHost)) return true
  if (searchParams.has('ref')) return true
  if (searchParams.get('utm_medium') === 'email') return false
  return [...searchParams.keys()].some((key) => key.startsWith('utm_'))
}
